import { UseFormReturn, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useAccount } from '@/domain/hooks/useAccount'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { TokenWithValue } from '@/domain/common/types'
import { usePageChainId } from '@/domain/hooks/usePageChainId'
import { useMarketInfo } from '@/domain/market-info/useMarketInfo'
import { Reserve } from '@/domain/market-info/marketInfo'
import { getChainConfigEntry } from '@/config/chain'
import { assert, raise } from '@/utils/assert'
import { NormalizedUnitNumber } from '@/domain/types/NumericValues'
import { formFormat } from '@/domain/common/format'
import { BigNumber } from 'bignumber.js'
import { useDebounce } from '@/utils/useDebounce'
import { useState } from 'react'
import { PageState, PageStatus } from '@/features/dialogs/common/types'
import { useL1Balances } from '@/domain/wallet/hypercore/useL1Balances'
import { SpotBalance } from '@nktkas/hyperliquid'
import { BridgeObjective } from '@/features/actions/flavours/bridge/types'
import { useHyperCoreSpotMeta } from '@/domain/wallet/hypercore/useHyperCoreSpotMeta'
import { HyperCoreAsset } from '@/domain/wallet/hypercore/querySpotMeta'
import { SpotToken } from '@nktkas/hyperliquid'
import { Token } from '@/domain/types/Token'
import { CheckedAddress } from '@/domain/types/CheckedAddress'
import { useEffect } from 'react'
import { useTokensInfo } from '@/domain/wallet/useTokens/useTokensInfo'
import { TokensInfo } from '@/domain/wallet/useTokens/TokenInfo'
import { TokenConfig } from '@/domain/wallet/useTokens/types'
import { MarketWalletInfo, useMarketWalletInfo } from '@/domain/wallet/useMarketWalletInfo'
import { l1BalancesQueryKey } from '@/domain/wallet/hypercore/query'
import { queryClient } from '@/config/query-client'
import { tokensQueryKey } from '@/domain/wallet/useTokens/query'
import { useSideDrawer } from '@/domain/hooks/useSideDrawer'

const BaseAssetInputSchema = z.object({
  symbol: z.string().transform(TokenSymbol),
  value: z.string().refine(() => true), // @note makes types consistent between input schemas and allows empty strings
})

export const AssetInputSchema = BaseAssetInputSchema.extend({
  value: z.string().refine(
    (data) => {
      if (data === '') {
        return true
      }

      const value = Number.parseFloat(data)
      return !Number.isNaN(value) && value > 0
    },
    {
      message: 'Value must be greater than zero',
    },
  ),
})

export type AssetInputSchema = z.infer<typeof AssetInputSchema>

function getFromFieldValidator({
  tokensInfo,
  walletInfo,
  l1Balances,
  swapMode,
}: { tokensInfo: TokensInfo; l1Balances: SpotBalance[]; swapMode: SwapMode; walletInfo: MarketWalletInfo }) {
  return AssetInputSchema.superRefine((field, ctx) => {
    const value = NormalizedUnitNumber(field.value || 0)
    const evmBalance =
      field.symbol === 'HYPE'
        ? walletInfo.findWalletBalanceForSymbol(TokenSymbol('HYPE'))
        : tokensInfo.findOneTokenWithBalanceBySymbolCaseInsensitive(TokenSymbol(field.symbol))?.balance
    const balance =
      swapMode === 'bridgeToHyperEVM'
        ? l1Balances.find(({ coin }) => coin === field.symbol)?.total ?? '0'
        : evmBalance ?? '0' // @TODO: make dynamic based on l1 vs evm
    // const isNativeAsset = marketInfo.nativeAssetInfo.nativeAssetSymbol === field.symbol

    const maxValue = getBridgeMaxValue({
      balance: NormalizedUnitNumber(balance),
      // isNativeAsset,
      // minRemainingNativeAsset: marketInfo.nativeAssetInfo.minRemainingNativeAssetBalance,
    })

    const issue = value.gt(maxValue)

    if (issue) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Insufficient balance',
        path: ['value'],
      })
    }
  })
}

function getBridgeFormValidator({
  tokensInfo,
  l1Balances,
  walletInfo,
  swapMode,
}: { tokensInfo: TokensInfo; l1Balances: SpotBalance[]; swapMode: SwapMode; walletInfo: MarketWalletInfo }) {
  return z.object({
    isMaxSelected: z.boolean(),
    swapMode: z.enum(['swap', 'wrap', 'unwrap', 'bridgeToHyperEVM', 'bridgeToHyperCore']),
    from: getFromFieldValidator({ tokensInfo, l1Balances, walletInfo, swapMode }),
    to: BaseAssetInputSchema,
  })
}

export type BridgeFormSchema = z.infer<ReturnType<typeof getBridgeFormValidator>>

export type SwapMode = 'swap' | 'wrap' | 'unwrap' | 'bridgeToHyperEVM' | 'bridgeToHyperCore'

export type SuccessData = (BridgeFormNormalizedData & { swapMode: SwapMode }) | null

export interface UseBridgeResults {
  form: UseFormReturn<BridgeFormSchema>
  pageStatus: PageStatus & { resetForm: () => void; successData: SuccessData }
  guestMode: boolean
  fromTokenField: UseBridgeFormFieldsResults
  toTokenField: UseBridgeFormFieldsResults
  fromToken: TokenWithValue
  toToken: TokenWithValue
  swapMode: SwapMode
  actions: BridgeObjective[]
  openConnectModal: () => void
  swapDirection: () => void
}

function nameOverride(name: string): string {
  switch (name) {
    case 'USDT0':
      return 'USD₮0'
    default:
      return name
  }
}

export function useBridge(): UseBridgeResults {
  const { chainId } = usePageChainId()
  const account = useAccount()
  const guestMode = !account
  const { open } = useSideDrawer()
  const { balances } = useL1Balances()
  const l1Balances = balances.map(({ coin, ...rest }) => ({
    coin: nameOverride(coin),
    ...rest,
  }))
  const { assets } = useHyperCoreSpotMeta()
  const hyperCoreAssets = assets.map(({ name, ...rest }) => ({
    name: nameOverride(name),
    ...rest,
  }))
  const [successData, setSuccessData] = useState<SuccessData>(null)
  const { marketInfo } = useMarketInfo({ chainId })
  const { markets } = getChainConfigEntry(chainId)
  const { nativeAssetInfo } = markets ?? {}
  const [pageStatus, setPageStatus] = useState<PageState>('form')
  const [currentSwapMode, setCurrentSwapMode] = useState<SwapMode>('bridgeToHyperEVM')
  const walletInfo = useMarketWalletInfo({ chainId })

  assert(nativeAssetInfo, 'Native asset info is required for swap')

  const tokensFormatted = hyperCoreAssets.flatMap(({ name, evmContract }) =>
    !evmContract?.address || name === nativeAssetInfo.nativeAssetSymbol
      ? []
      : ({
          address: CheckedAddress(evmContract.address),
          oracleType: 'zero-price',
        } as TokenConfig),
  )

  const { tokensInfo } = useTokensInfo({
    tokens: tokensFormatted,
    chainId,
  })

  const bridgeAssets = getBridgeAssetsHyperCore(
    [...marketInfo.reserves, marketInfo.findOneReserveBySymbol(nativeAssetInfo.nativeAssetSymbol)],
    hyperCoreAssets,
    tokensInfo,
    l1Balances,
    walletInfo,
  )

  const form = useForm<BridgeFormSchema>({
    resolver: zodResolver(getBridgeFormValidator({ tokensInfo, l1Balances, swapMode: currentSwapMode, walletInfo })),
    defaultValues: {
      ...getDefaultFormValues(bridgeAssets),
      swapMode: currentSwapMode,
    },
    mode: 'onChange',
  })

  // Watch for changes in the swap mode
  const formSwapMode = form.watch('swapMode')

  // Keep currentSwapMode in sync with form's swapMode
  useEffect(() => {
    if (formSwapMode && formSwapMode !== currentSwapMode) {
      setCurrentSwapMode(formSwapMode)
    }
  }, [formSwapMode, currentSwapMode])

  const rawFormValues = form.watch()
  const formValues = normalizeFormValues(rawFormValues, bridgeAssets)

  const { debouncedFormValues, isDebouncing, isFormValid } = useDebouncedDialogFormValues({
    form,
    bridgeAssets,
  })

  const actions = useCreateObjectives(debouncedFormValues, bridgeAssets, currentSwapMode)

  function createCustomOnChange(fieldName: 'from' | 'to') {
    return (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value
      form.setValue(`${fieldName}.value`, value)
      form.trigger().catch(console.error)
    }
  }

  const fromTokenField = useBridgeFormFields({
    form,
    assets: bridgeAssets,
    fieldName: 'from',
    tokensInfo,
    l1Balances,
    onChange: createCustomOnChange('to'),
    swapMode: currentSwapMode,
    counterpartFieldName: 'to',
  })

  const toTokenField = useBridgeFormFields({
    form,
    assets: bridgeAssets,
    fieldName: 'to',
    tokensInfo,
    l1Balances,
    onChange: createCustomOnChange('from'),
    swapMode: currentSwapMode,
    counterpartFieldName: 'from',
  })

  function handleSwapDirection(): void {
    // Update the swap mode in the form
    const newSwapMode = currentSwapMode === 'bridgeToHyperEVM' ? 'bridgeToHyperCore' : 'bridgeToHyperEVM'
    form.setValue('swapMode', newSwapMode)
    setCurrentSwapMode(newSwapMode)

    // Clear values to avoid validation errors with previous values that might exceed new max
    form.setValue('from.value', '')
    form.setValue('to.value', '')
    form.setValue('isMaxSelected', false)

    // Re-trigger validation
    form.trigger().catch(console.error)
  }

  return {
    form,
    swapMode: currentSwapMode,
    actions,
    fromTokenField,
    toTokenField,
    fromToken: formValues.from,
    toToken: formValues.to,
    pageStatus: {
      state: pageStatus,
      actionsEnabled: debouncedFormValues.from.value.gt(0) && isFormValid && !isDebouncing,
      successData,
      goToSuccessScreen: () => {
        setSuccessData({ ...formValues, swapMode: currentSwapMode })
        setPageStatus('success')
        form.reset(getDefaultFormValues(bridgeAssets))
        void queryClient.refetchQueries({
          queryKey: tokensQueryKey({
            tokens: tokensFormatted,
            account,
            chainId,
          }),
        })
        void queryClient.refetchQueries({
          queryKey: l1BalancesQueryKey({
            account,
          }),
        })
      },
      resetForm: () => {
        form.reset(getDefaultFormValues(bridgeAssets))
        setPageStatus('form')
        setSuccessData(null)
      },
    },
    guestMode,
    swapDirection: handleSwapDirection,
    openConnectModal: open,
  }
}

export type BridgeToken = SpotToken & {
  token: Token
  l1Balance: NormalizedUnitNumber
  systemAddress: string
  balance: NormalizedUnitNumber
  value: NormalizedUnitNumber
  tokenId: string
}
export interface UseDebouncedFormValuesArgs {
  form: UseFormReturn<BridgeFormSchema>
  bridgeAssets: BridgeToken[]
}
export interface UseDebouncedDialogFormValuesResult {
  debouncedFormValues: BridgeFormNormalizedData
  isFormValid: boolean
  isDebouncing: boolean
}
export function useDebouncedDialogFormValues({
  form,
  bridgeAssets,
}: UseDebouncedFormValuesArgs): UseDebouncedDialogFormValuesResult {
  const formValues = normalizeFormValues(form.watch(), bridgeAssets)
  const isFormValid = form.formState.isValid
  const { debouncedValue, isDebouncing } = useDebounce(
    { formValues, isFormValid },
    getNormalizedFormValuesKey(formValues) + isFormValid.toString(),
  )

  return {
    debouncedFormValues: debouncedValue.formValues,
    isFormValid: debouncedValue.isFormValid,
    isDebouncing,
  }
}

function getNormalizedFormValuesKey(values: BridgeFormNormalizedData): string {
  return [values.from.token.address, values.from.value.toFixed(), values.from.tokenId].join('-')
}

function getTokenFromSpotToken(
  reserves: Reserve[],
  coreAsset: HyperCoreAsset,
  tokensInfo: TokensInfo,
  l1Balances: SpotBalance[],
  walletInfo: MarketWalletInfo,
): BridgeToken {
  const priceInUSD =
    reserves
      .find((reserve) => reserve.token.symbol.toUpperCase() === TokenSymbol(coreAsset.name))
      ?.priceInUSD.toString() ?? '0'

  const token = new Token({
    symbol: TokenSymbol(coreAsset.name),
    decimals: coreAsset.weiDecimals + (coreAsset?.evmContract?.evm_extra_wei_decimals ?? 0),
    address: CheckedAddress(coreAsset.evmContract?.address ?? '0x0'),
    unitPriceUsd: priceInUSD,
    name: coreAsset.name,
    isAToken: false,
  })

  const spotToken: BridgeToken = {
    ...coreAsset,
    token,
    evmContract: coreAsset.evmContract,
    systemAddress: coreAsset.systemAddress,
    l1Balance: NormalizedUnitNumber(l1Balances.find(({ coin }) => coin === coreAsset.name)?.total ?? '0'),
    balance:
      coreAsset.name === 'HYPE'
        ? walletInfo.findWalletBalanceForSymbol(TokenSymbol('HYPE'))
        : tokensInfo.findOneTokenWithBalanceBySymbolCaseInsensitive(TokenSymbol(coreAsset.name))?.balance ??
          NormalizedUnitNumber(0),
    value: NormalizedUnitNumber(0),
  }
  return spotToken
}

export function hasL1Balance(r: HyperCoreAsset, l1Balances: SpotBalance[]): boolean {
  return NormalizedUnitNumber(l1Balances.find(({ coin }) => coin === r.name)?.total ?? '0').gt(0)
}

export function hasEvmBalance(r: HyperCoreAsset, tokensInfo: TokensInfo, walletInfo: MarketWalletInfo): boolean {
  return (
    !!tokensInfo.findOneTokenWithBalanceBySymbolCaseInsensitive(TokenSymbol(r.name))?.balance.gt(0) ||
    walletInfo.findWalletBalanceForSymbol(TokenSymbol(r.name)).gt(0)
  )
}

export function getBridgeAssetsHyperCore(
  reserves: Reserve[],
  hyperCoreAssets: HyperCoreAsset[],
  tokensInfo: TokensInfo,
  l1Balances: SpotBalance[],
  walletInfo: MarketWalletInfo,
): BridgeToken[] {
  const whitelistedBridgePairs = reserves.map(({ token }) => token.symbol)
  return hyperCoreAssets
    .filter(
      (coreAsset: HyperCoreAsset) =>
        hasL1Balance(coreAsset, l1Balances) ||
        hasEvmBalance(coreAsset, tokensInfo, walletInfo) ||
        whitelistedBridgePairs.includes(TokenSymbol(coreAsset.name)),
    )
    .map((coreAsset) => getTokenFromSpotToken(reserves, coreAsset, tokensInfo, l1Balances, walletInfo))
    .reverse()
}

export function getDefaultFormValues(availableTokens: any[]): BridgeFormSchema {
  const defaultFromToken = availableTokens[0]!

  return {
    from: { symbol: defaultFromToken.token.symbol, value: '' },
    to: { symbol: defaultFromToken.token.symbol, value: '' },
    isMaxSelected: false,
    swapMode: 'bridgeToHyperEVM' as SwapMode,
  }
}

interface UseBridgeFormFieldsArgs {
  form: UseFormReturn<BridgeFormSchema>
  assets: BridgeToken[]
  fieldName: 'from' | 'to'
  counterpartFieldName: 'to' | 'from'
  tokensInfo: TokensInfo
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  l1Balances: SpotBalance[]
  swapMode: SwapMode
}
export interface UseBridgeFormFieldsResults {
  selectedAsset: BridgeToken
  availableAssets: BridgeToken[]
  changeToken: (newSymbol: TokenSymbol) => void
  maxSelectedFieldName: string | undefined
  maxValue: NormalizedUnitNumber
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onSetMax: ((max: NormalizedUnitNumber) => void) | undefined
}

export function useBridgeFormFields({
  form,
  assets,
  fieldName,
  counterpartFieldName,
  onChange,
  swapMode,
}: UseBridgeFormFieldsArgs): UseBridgeFormFieldsResults {
  const formValues = form.getValues()
  const currentSymbol = formValues[fieldName].symbol

  const selectedAsset = assets.find(({ token }) => token.symbol === currentSymbol)
  assert(selectedAsset, `Selected asset not found: ${currentSymbol}`)

  function changeToken(newSymbol: TokenSymbol): void {
    form.setValue(fieldName, { symbol: newSymbol, value: '' }, { shouldValidate: false })
    form.setValue(counterpartFieldName, { symbol: newSymbol, value: '' }, { shouldValidate: false })
    form.setValue('isMaxSelected', false)
  }

  const maxValue =
    fieldName === 'from'
      ? getBridgeMaxValue({
          balance: swapMode === 'bridgeToHyperEVM' ? selectedAsset.l1Balance : selectedAsset.balance, // @TODO: make dynamic based on l1 vs evm
          // isNativeAsset: marketInfo.nativeAssetInfo.nativeAssetSymbol === currentSymbol,
          // minRemainingNativeAsset: marketInfo.nativeAssetInfo.minRemainingNativeAssetBalance,
        })
      : NormalizedUnitNumber(0)

  return {
    selectedAsset,
    availableAssets: assets,
    onChange,
    changeToken,
    maxSelectedFieldName: fieldName === 'from' ? 'isMaxSelected' : undefined,
    maxValue,
    onSetMax:
      fieldName === 'from'
        ? (max: NormalizedUnitNumber) => form.setValue('to.value', formFormat(max, selectedAsset.token.decimals))
        : undefined,
  }
}

function getBridgeMaxValue({
  balance,
  // isNativeAsset,
  // minRemainingNativeAsset,
}: {
  balance: NormalizedUnitNumber
  // isNativeAsset: boolean
  // minRemainingNativeAsset: NormalizedUnitNumber
}): NormalizedUnitNumber {
  // const balanceBasedMaxDeposit = balance.minus(isNativeAsset ? minRemainingNativeAsset : NormalizedUnitNumber(0))

  return NormalizedUnitNumber(BigNumber.max(balance, 0))
}

export interface BridgeFormNormalizedData {
  from: BridgeToken
  to: BridgeToken
}

export function normalizeFormValues(values: BridgeFormSchema, formAssets: BridgeToken[]): BridgeFormNormalizedData {
  function normalizeAsset(assetInput: { symbol: string; value: string }): BridgeToken {
    const bridgeToken = formAssets.find(({ token }) => token.symbol === assetInput.symbol) ?? raise('Asset not found')
    return {
      ...bridgeToken,
      value: NormalizedUnitNumber(assetInput.value === '' ? '0' : assetInput.value),
    }
  }

  return {
    from: normalizeAsset(values.from),
    to: normalizeAsset(values.to),
  }
}

export function useCreateObjectives(
  formValues: BridgeFormNormalizedData,
  assets: BridgeToken[],
  swapMode: SwapMode,
): BridgeObjective[] {
  const fromToken =
    assets.find(({ token }) => token.symbol === formValues.from.token.symbol) ?? raise('Asset not found')

  return [
    {
      type: swapMode as any,
      from: fromToken,
      amount: formValues.from.value ?? NormalizedUnitNumber(0),
    },
  ]
}
